/**
 * Fletcher's latest check of a ticket's pull request, for the ticket's
 * Fletcher card and its Receipt (YAG-1077):
 *
 *   `GET /api/work-items/:id/qa` → `{ check }`
 *
 * It reads again when the check's case or the ticket moves (a run starting or
 * finishing, the item changing), when the row's QA facet changes
 * (`refreshKey`), after Fix these or Recheck (`reread`), and every
 * `LIVE_RUN_POLL_MS` while Fletcher is testing. A 404 or `{ check: null }` is
 * no check; a failed read keeps what the last good one showed and reports
 * the first failure only.
 */
import { useCallback, useEffect, useState } from "react";
import type { TicketQaCheck, TicketQaCheckResponse } from "@yagni/shared";
import { captureUiError } from "../sentry.js";
import { useRunLiveFlip } from "./useRunLiveFlip.js";
import { useWorkItemEntityChange } from "./useWorkItemEntityChange.js";
import { LIVE_RUN_POLL_MS } from "./useWorkItemRuns.js";

export interface TicketQaCheckRead {
  check: TicketQaCheck | null;
  /** Read it again now: after Fix these or Recheck. */
  reread: () => void;
}

export function useTicketQaCheck(
  workItemId: string,
  /** False where no check can exist (not a ticket, no pull request): no request. */
  enabled: boolean,
  /** Changes when the row's QA facet does, so a new check is read as it starts. */
  refreshKey = "",
): TicketQaCheckRead {
  const [check, setCheck] = useState<TicketQaCheck | null>(null);
  const [nonce, setNonce] = useState(0);
  const reread = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    if (!enabled) {
      setCheck(null);
      return;
    }
    let cancelled = false;
    fetch(`/api/work-items/${encodeURIComponent(workItemId)}/qa`, { credentials: "include" })
      .then((res) => {
        if (res.status === 404) return { check: null } satisfies TicketQaCheckResponse;
        if (!res.ok) throw new Error(`Fletcher check read failed (${res.status})`);
        return res.json() as Promise<TicketQaCheckResponse>;
      })
      .then((body) => {
        if (!cancelled) setCheck(body && typeof body === "object" ? (body.check ?? null) : null);
      })
      .catch((err: unknown) => {
        if (nonce === 0) {
          captureUiError(err, {
            area: "Work",
            operation: "read_ticket_qa_check",
            severity: "warning",
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [workItemId, enabled, nonce, refreshKey]);

  // A check starting or finishing moves its case's run; a new one is filed on the ticket.
  const caseId = enabled ? (check?.caseId ?? null) : null;
  useRunLiveFlip(caseId, reread);
  useWorkItemEntityChange(caseId, reread);
  useWorkItemEntityChange(enabled ? workItemId : null, reread);

  const testing = enabled && check?.state === "testing";
  useEffect(() => {
    if (!testing) return;
    const timer = setInterval(reread, LIVE_RUN_POLL_MS);
    return () => clearInterval(timer);
  }, [testing, reread]);

  return { check: enabled ? check : null, reread };
}
